// ══════════════════════════════════════════════════════════
// STATIONS SERVICE — Supabase Direct + Backend PHP Contract
//
// Supabase para CRUD normal de estaciones (RLS).
// Backend PHP (rfwBackend) solo para operaciones sensibles:
//   - probar proveedor
//   - aprovisionar estación
//   - crear cuenta CentovaCast
//   - consultar salud del proveedor
//   - procesar credenciales
//   - sincronizar mounts
//   - start / stop / restart / reload
//
// NO llama a CentovaCast desde frontend.
// NO llama a AzuraCast desde frontend.
// NUNCA expone passwords, tokens, ni credenciales.
// ══════════════════════════════════════════════════════════

import { getSupabaseClient } from '@/services/supabase/client';
import { backendApiRequest, RfwBackendError } from '@/services/rfwBackend';
import type {
  Station,
  StationInsert,
  StationUpdate,
  StationStatus,
  StationSettings,
  StationSettingsUpdate,
  ProviderAccountSafe,
  StreamServer,
  StreamMount,
  StationControlOperation,
  StationControlResult,
  ProviderOperationalStatus,
  StationSyncRequestResult,
  StationSyncStatus,
  StationSyncConflict,
  StreamHealthStatus,
  StreamHealthResult,
} from '@/types/stations';

// ── HELPERS ──

function supabase() {
  return getSupabaseClient();
}

// ═══════════════ STATIONS CRUD (Supabase Direct) ═══════════════

/**
 * Listar estaciones de una radio.
 * RLS: anon ve estaciones de radios públicas activas.
 *      Miembros ven las de su radio.
 */
export async function listStations(radioId: string): Promise<Station[]> {
  const { data, error } = await supabase()
    .from('stations')
    .select('*')
    .eq('radio_id', radioId)
    .order('is_default', { ascending: false })
    .order('created_at', { ascending: false });

  if (error) throw new Error(`Error al listar estaciones: ${error.message}`);
  return (data as Station[]) || [];
}

/**
 * Obtener una estación por ID.
 */
export async function getStation(id: string): Promise<Station | null> {
  const { data, error } = await supabase()
    .from('stations')
    .select('*')
    .eq('id', id)
    .maybeSingle();

  if (error) throw new Error(`Error al obtener estación: ${error.message}`);
  return (data as Station) || null;
}

/**
 * Obtener una estación por slug.
 */
export async function getStationBySlug(
  radioId: string,
  slug: string,
): Promise<Station | null> {
  const { data, error } = await supabase()
    .from('stations')
    .select('*')
    .eq('radio_id', radioId)
    .eq('slug', slug)
    .maybeSingle();

  if (error) throw new Error(`Error al obtener estación por slug: ${error.message}`);
  return (data as Station) || null;
}

/**
 * Crear una estación en estado inicial NOT_CONFIGURED.
 * RLS: solo radio owner/admin puede insertar.
 */
export async function createStation(input: StationInsert): Promise<Station> {
  const { data, error } = await supabase()
    .from('stations')
    .insert({
      ...input,
      status: 'NOT_CONFIGURED' as StationStatus,
    })
    .select('*')
    .single();

  if (error) throw new Error(`Error al crear estación: ${error.message}`);
  return data as Station;
}

/**
 * Actualizar datos editoriales de una estación.
 * RLS: solo radio owner/admin puede actualizar.
 * NO permite cambiar status — eso lo maneja el backend PHP o admin.
 */
export async function updateStation(
  id: string,
  input: StationUpdate,
): Promise<Station> {
  const { data, error } = await supabase()
    .from('stations')
    .update(input)
    .eq('id', id)
    .select('*')
    .single();

  if (error) throw new Error(`Error al actualizar estación: ${error.message}`);
  return data as Station;
}

/**
 * Obtener solo el estado de una estación.
 */
export async function getStationStatus(id: string): Promise<StationStatus | null> {
  const { data, error } = await supabase()
    .from('stations')
    .select('status')
    .eq('id', id)
    .maybeSingle();

  if (error) throw new Error(`Error al obtener estado de estación: ${error.message}`);
  return (data as { status: StationStatus } | null)?.status ?? null;
}

/**
 * Obtener la estación por defecto de una radio.
 */
export async function getDefaultStation(radioId: string): Promise<Station | null> {
  const { data, error } = await supabase()
    .from('stations')
    .select('*')
    .eq('radio_id', radioId)
    .eq('is_default', true)
    .maybeSingle();

  if (error) throw new Error(`Error al obtener estación por defecto: ${error.message}`);
  return (data as Station) || null;
}

// ═══════════════ STATION SETTINGS ═══════════════

/**
 * Obtener configuración de una estación.
 * RLS: solo station admins pueden leer.
 */
export async function getStationSettings(
  stationId: string,
): Promise<StationSettings | null> {
  const { data, error } = await supabase()
    .from('station_settings')
    .select('*')
    .eq('station_id', stationId)
    .maybeSingle();

  if (error) throw new Error(`Error al obtener configuración de estación: ${error.message}`);
  return (data as StationSettings) || null;
}

/**
 * Upsert de configuración de estación.
 * RLS: solo station admins pueden escribir.
 */
export async function upsertStationSettings(
  stationId: string,
  input: StationSettingsUpdate,
): Promise<StationSettings> {
  const existing = await getStationSettings(stationId);

  if (existing) {
    const { data, error } = await supabase()
      .from('station_settings')
      .update(input)
      .eq('station_id', stationId)
      .select('*')
      .single();

    if (error) throw new Error(`Error al actualizar configuración de estación: ${error.message}`);
    return data as StationSettings;
  }

  const { data, error } = await supabase()
    .from('station_settings')
    .insert({ station_id: stationId, settings: input.settings })
    .select('*')
    .single();

  if (error) throw new Error(`Error al crear configuración de estación: ${error.message}`);
  return data as StationSettings;
}

// ═══════════════ PROVIDER ACCOUNTS (Metadata segura) ═══════════════

/**
 * Obtener metadata segura de las cuentas de proveedor de una organización.
 * NUNCA retorna: password_encrypted, username, host, port.
 * RLS: solo org owners/admins/managers pueden leer.
 */
export async function getProviderAccounts(
  organizationId: string,
): Promise<ProviderAccountSafe[]> {
  const { data, error } = await supabase()
    .from('provider_accounts')
    .select('id, organization_id, provider_type, name, is_active, metadata, created_at, updated_at')
    .eq('organization_id', organizationId)
    .eq('is_active', true)
    .order('provider_type')
    .order('name');

  if (error) throw new Error(`Error al obtener cuentas de proveedor: ${error.message}`);
  return (data as ProviderAccountSafe[]) || [];
}

// ═══════════════ STREAM SERVERS ═══════════════

/**
 * Obtener servidores de streaming asociados a una radio.
 */
export async function getStreamServers(radioId: string): Promise<StreamServer[]> {
  const { data, error } = await supabase()
    .from('stream_servers')
    .select('*')
    .eq('radio_id', radioId)
    .eq('is_active', true)
    .order('name');

  if (error) throw new Error(`Error al obtener servidores de streaming: ${error.message}`);
  return (data as StreamServer[]) || [];
}

// ═══════════════ STREAM MOUNTS ═══════════════

/**
 * Obtener mounts de un servidor de streaming.
 */
export async function getStreamMounts(serverId: string): Promise<StreamMount[]> {
  const { data, error } = await supabase()
    .from('stream_mounts')
    .select('*')
    .eq('server_id', serverId)
    .eq('is_active', true)
    .order('is_default', { ascending: false })
    .order('mount_point');

  if (error) throw new Error(`Error al obtener mounts de streaming: ${error.message}`);
  return (data as StreamMount[]) || [];
}

// ════════════════════════════════════════════════
// BACKEND PHP CONTRACT — Métodos preparados
//
// Estos métodos están tipados y listos para cuando
// el backend PHP exponga los endpoints correspondientes.
// NO se ejecutan automáticamente.
//
// Responsabilidades del backend PHP:
//   - Probar conectividad con proveedores
//   - Aprovisionar estaciones (CentovaCast, AzuraCast)
//   - Crear cuentas en CentovaCast
//   - Consultar salud del proveedor
//   - Procesar credenciales de streaming
//   - Sincronizar mounts
//   - Control de estación: START, STOP, RESTART, RELOAD
// ════════════════════════════════════════════════

// ── Tipos para el contrato backend ──

export interface ProvisionStationRequest {
  station_id: string;
  provider_account_id: string;
  server_id?: string;
  mount_point?: string;
  format?: string;
  bitrate?: number;
  listeners_max?: number;
}

export interface ProvisionStationResponse {
  station_id: string;
  centovacast_id: string;
  stream_url: string;
  mount_point: string;
  admin_url: string;
}

export interface ProviderTestRequest {
  provider_account_id: string;
}

export interface ProviderTestResponse {
  provider_account_id: string;
  reachable: boolean;
  latency_ms: number;
  server_type: string;
  server_version: string;
  accounts_count: number;
}

export interface ProviderHealthResponse {
  provider_account_id: string;
  status: 'healthy' | 'degraded' | 'down';
  uptime_percent: number;
  active_accounts: number;
  total_accounts: number;
  last_check: string;
}

export interface SyncMountsRequest {
  station_id: string;
  server_id: string;
}

export interface SyncMountsResponse {
  station_id: string;
  mounts: Array<{
    mount_point: string;
    format: string;
    bitrate: number;
    listeners: number;
    status: string;
  }>;
}

// ── Helpers de control ──

/**
 * Traduce un código HTTP del backend PHP a un mensaje
 * de error amigable para el usuario.
 * No expone respuestas crudas del proveedor (CentovaCast, etc).
 */
function mapControlError(err: unknown): never {
  if (err instanceof RfwBackendError) {
    switch (err.statusCode) {
      case 401:
        throw new Error('Tu sesión expiró. Volvé a iniciar sesión para controlar la estación.');
      case 403:
        throw new Error('No tenés permisos para controlar esta estación.');
      case 404:
        throw new Error('Estación no encontrada. Verificá que exista y esté configurada.');
      case 409:
        throw new Error('La estación no está aprovisionada o el proveedor no está configurado. Completá la configuración antes de controlarla.');
      case 422:
        throw new Error('Esta operación no está soportada para esta estación o proveedor.');
      case 429:
        throw new Error('Demasiadas solicitudes. Esperá unos segundos antes de intentar de nuevo.');
      default:
        if (err.statusCode >= 500) {
          throw new Error('El proveedor de streaming devolvió un error. Revisá el estado del servidor e intentá de nuevo.');
        }
        throw new Error(err.message || 'Error al ejecutar la operación de control.');
    }
  }
  throw err instanceof Error ? err : new Error('Error desconocido al controlar la estación.');
}

/**
 * Traduce errores HTTP de sync a mensajes amigables.
 * Nunca muestra respuestas crudas del proveedor, credenciales,
 * ni información administrativa del host remoto.
 */
function mapSyncError(err: unknown): never {
  if (err instanceof RfwBackendError) {
    switch (err.statusCode) {
      case 401:
        throw new Error('Tu sesión expiró. Volvé a iniciar sesión para sincronizar la estación.');
      case 403:
        throw new Error('No tenés permisos para sincronizar esta estación.');
      case 404:
        throw new Error('Estación no encontrada. Verificá que exista.');
      case 409:
        throw new Error('La estación no tiene un proveedor configurado. Completá la configuración antes de sincronizar.');
      case 422:
        throw new Error('La sincronización no está soportada para este tipo de proveedor.');
      case 429:
        throw new Error('Demasiadas solicitudes. Esperá unos segundos antes de intentar de nuevo.');
      default:
        if (err.statusCode >= 500) {
          throw new Error('El proveedor de streaming devolvió un error durante la sincronización. Revisá el estado del servidor e intentá de nuevo.');
        }
        throw new Error(err.message || 'Error al sincronizar la estación.');
    }
  }
  throw err instanceof Error ? err : new Error('Error desconocido al sincronizar la estación.');
}

/**
 * Ejecuta una operación de control contra el backend PHP.
 * No envía provider_id, credenciales, ni métodos CentovaCast.
 * No asume éxito con actualización optimista.
 */
async function executeControl(
  stationId: string,
  operation: StationControlOperation,
): Promise<StationControlResult> {
  try {
    const response = await backendApiRequest<StationControlResult>(
      `/api/v1/stations/${encodeURIComponent(stationId)}/${operation.toLowerCase()}`,
      { method: 'POST' },
    );
    return response.data;
  } catch (err: unknown) {
    mapControlError(err);
    // mapControlError siempre lanza, esta línea nunca se alcanza
    throw err;
  }
}

/**
 * Contrato de métodos del backend PHP para estaciones.
 *
 * Cada método retorna una promesa tipada. Para usarlos,
 * el backend PHP debe tener los endpoints implementados.
 *
 * Uso:
 *   import { stationsBackend } from '@/services/stations';
 *   const result = await stationsBackend.startStation('station-uuid');
 */
export const stationsBackend = {
  /**
   * Iniciar transmisión de una estación.
   * POST /api/v1/stations/{stationId}/start
   */
  startStation(stationId: string): Promise<StationControlResult> {
    return executeControl(stationId, 'START');
  },

  /**
   * Detener transmisión de una estación.
   * POST /api/v1/stations/{stationId}/stop
   *
   * ⚠️ Requiere confirmación del usuario antes de llamar.
   */
  stopStation(stationId: string): Promise<StationControlResult> {
    return executeControl(stationId, 'STOP');
  },

  /**
   * Reiniciar transmisión de una estación.
   * POST /api/v1/stations/{stationId}/restart
   *
   * ⚠️ Requiere confirmación del usuario antes de llamar.
   */
  restartStation(stationId: string): Promise<StationControlResult> {
    return executeControl(stationId, 'RESTART');
  },

  /**
   * Recargar configuración del servidor de streaming.
   * POST /api/v1/stations/{stationId}/reload
   *
   * ⚠️ Requiere confirmación del usuario antes de llamar.
   * No interrumpe la transmisión en curso.
   */
  reloadStation(stationId: string): Promise<StationControlResult> {
    return executeControl(stationId, 'RELOAD');
  },

  /**
   * Probar conectividad con un proveedor.
   * POST /api/v1/stations/provider/test
   */
  testProvider: (_req: ProviderTestRequest): Promise<ProviderTestResponse> => {
    throw new Error(
      '[stationsBackend] testProvider: endpoint no disponible. ' +
      'El backend PHP debe implementar POST /api/v1/stations/provider/test',
    );
  },

  /**
   * Aprovisionar una estación en CentovaCast.
   * Crea la cuenta, configura el mount, devuelve URLs de streaming.
   * POST /api/v1/stations/provision
   */
  provisionStation: (_req: ProvisionStationRequest): Promise<ProvisionStationResponse> => {
    throw new Error(
      '[stationsBackend] provisionStation: endpoint no disponible. ' +
      'El backend PHP debe implementar POST /api/v1/stations/provision',
    );
  },

  /**
   * Crear una cuenta en CentovaCast para la estación.
   * POST /api/v1/stations/centova/account
   */
  createCentovaAccount: (_stationId: string): Promise<{ centovacast_id: string; admin_url: string }> => {
    throw new Error(
      '[stationsBackend] createCentovaAccount: endpoint no disponible. ' +
      'El backend PHP debe implementar POST /api/v1/stations/centova/account',
    );
  },

  /**
   * Consultar salud del proveedor (CentovaCast/AzuraCast).
   * GET /api/v1/stations/provider/health/:id
   */
  getProviderHealth: (_providerAccountId: string): Promise<ProviderHealthResponse> => {
    throw new Error(
      '[stationsBackend] getProviderHealth: endpoint no disponible. ' +
      'El backend PHP debe implementar GET /api/v1/stations/provider/health/:id',
    );
  },

  /**
   * Procesar credenciales de streaming de forma segura.
   * El backend recibe las credenciales y las almacena sin exponerlas.
   * POST /api/v1/stations/credentials
   */
  processCredentials: (_stationId: string, _credentials: Record<string, string>): Promise<{ success: boolean }> => {
    throw new Error(
      '[stationsBackend] processCredentials: endpoint no disponible. ' +
      'El backend PHP debe implementar POST /api/v1/stations/credentials',
    );
  },

  /**
   * Sincronizar stream mounts desde el proveedor.
   * POST /api/v1/stations/mounts/sync
   */
  syncMounts: (_req: SyncMountsRequest): Promise<SyncMountsResponse> => {
    throw new Error(
      '[stationsBackend] syncMounts: endpoint no disponible. ' +
      'El backend PHP debe implementar POST /api/v1/stations/mounts/sync',
    );
  },

  /**
   * Sincronizar metadata de la estación con el proveedor.
   * POST /api/v1/stations/{stationId}/sync
   *
   * El backend responde HTTP 202 y encola el trabajo.
   * Usar getStationSyncStatus para consultar el progreso.
   *
   * ⚠️ Puede devolver CONFLICT si hay divergencia entre
   *    los datos locales y los del proveedor remoto.
   *    En ese caso no se sobrescribe nada automáticamente.
   */
  async syncStation(stationId: string): Promise<StationSyncRequestResult> {
    try {
      const response = await backendApiRequest<StationSyncRequestResult>(
        `/api/v1/stations/${encodeURIComponent(stationId)}/sync`,
        { method: 'POST' },
      );
      return response.data;
    } catch (err: unknown) {
      mapSyncError(err);
      throw err;
    }
  },

  /**
   * Consultar estado de la última sincronización.
   * GET /api/v1/stations/{stationId}/sync
   *
   * Devuelve el estado actual del job de sync:
   * PENDING, RUNNING, COMPLETED, FAILED, CONFLICT, o NOT_CONFIGURED.
   */
  async getStationSyncStatus(stationId: string): Promise<StationSyncRequestResult> {
    try {
      const response = await backendApiRequest<StationSyncRequestResult>(
        `/api/v1/stations/${encodeURIComponent(stationId)}/sync`,
        { method: 'GET' },
      );
      return response.data;
    } catch (err: unknown) {
      mapSyncError(err);
      throw err;
    }
  },
};

// ══════════════════════════════════════════════════════════
// STREAM HEALTH MONITORING — Backend PHP Contract
//
// requestStationHealthCheck → encola un health check en el backend.
// getStationHealthMetadata → lee metadata desde Supabase (stations.metadata).
//
// NO ejecuta pruebas de red desde el navegador.
// NO acepta URLs arbitrarias.
// NO expone hosts internos ni información SSRF.
// ══════════════════════════════════════════════════════════

/**
 * Traduce errores HTTP de health check a mensajes amigables.
 * Nunca muestra respuestas crudas del proveedor, hosts internos,
 * ni información que pueda facilitar SSRF.
 */
function mapHealthError(err: unknown): never {
  if (err instanceof RfwBackendError) {
    switch (err.statusCode) {
      case 401:
        throw new Error('Tu sesión expiró. Volvé a iniciar sesión para revisar la salud del stream.');
      case 403:
        throw new Error('No tenés permisos para revisar la salud de esta estación.');
      case 404:
        throw new Error('Estación no encontrada. Verificá que exista.');
      case 409:
        throw new Error('La estación no tiene un proveedor configurado. Completá la configuración antes de revisar la salud.');
      case 422:
        throw new Error('El health check no está soportado para este tipo de proveedor.');
      case 429:
        throw new Error('Demasiadas solicitudes. Esperá unos segundos antes de intentar de nuevo.');
      default:
        if (err.statusCode >= 500) {
          throw new Error('El servicio de monitoreo encontró un error. Revisá el estado del servidor e intentá de nuevo.');
        }
        throw new Error(err.message || 'Error al ejecutar el health check.');
    }
  }
  throw err instanceof Error ? err : new Error('Error desconocido al ejecutar el health check.');
}

/**
 * Agrega los métodos de stream health al contrato del backend.
 */
export const streamHealthBackend = {
  /**
   * Encola un health check para la estación.
   * POST /api/v1/stations/{stationId}/health-check
   *
   * Responde HTTP 202 — el trabajo se encola y se procesa
   * en background. Los resultados se persisten en Supabase
   * (stations.metadata) y se leen con getStationHealthMetadata.
   *
   * NO hace fetch directo al stream desde el navegador.
   * NO envía hostname, IP, ni credenciales en el payload.
   * NO acepta URLs arbitrarias del usuario.
   */
  async requestStationHealthCheck(stationId: string): Promise<StreamHealthResult> {
    try {
      const response = await backendApiRequest<StreamHealthResult>(
        `/api/v1/stations/${encodeURIComponent(stationId)}/health-check`,
        { method: 'POST' },
      );
      return response.data;
    } catch (err: unknown) {
      mapHealthError(err);
      throw err;
    }
  },

  /**
   * Lee la metadata de salud más reciente desde Supabase.
   * Consulta stations.metadata donde el backend PHP escribe
   * los resultados de los health checks.
   *
   * Retorna StreamHealthResult con los datos consolidados:
   * estado, última revisión, tiempo de respuesta, errores,
   * recuperación, configuración del proveedor y mount.
   *
   * Si no hay datos previos, retorna status UNKNOWN.
   */
  async getStationHealthMetadata(stationId: string): Promise<StreamHealthResult> {
    const { data, error } = await supabase()
      .from('stations')
      .select('metadata')
      .eq('id', stationId)
      .maybeSingle();

    if (error) throw new Error(`Error al leer metadata de salud: ${error.message}`);

    const metadata = (data as { metadata: Record<string, unknown> | null } | null)?.metadata;
    const health = (metadata?.stream_health as StreamHealthResult | undefined) ?? null;

    if (health) return health;

    // Sin datos previos → UNKNOWN
    return {
      station_id: stationId,
      status: 'UNKNOWN',
      last_check: '',
      response_time_ms: null,
      error_sanitized: null,
      recovery_detected: false,
      provider_configured: false,
      mount_available: false,
      message: 'No hay datos de salud disponibles. Ejecutá un health check para obtener el primer diagnóstico.',
    };
  },
};